import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { X, Maximize, Minimize, Plus, History, MessageCircle, Loader2, Pen, Copy, ThumbsUp, ThumbsDown, BookOpen } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { ChatInputBar } from "@/components/chat/chat-input/ChatInputBar";
import { useTranslation } from "@/context/LanguageContext";
import { useRoomContentText } from "@/hooks/useRoomContentText";
import { supabase } from "@/integrations/supabase/client";
import { loadRoomChatMessages, saveRoomChatMessage, clearRoomChatMessages } from "@/lib/chat/roomChatMessages";
import { RoomContentMentionPicker } from "./RoomContentMentionPicker";
import { FeedbackPopover } from "@/components/FeedbackPopover";
import { CitationBadge } from "@/components/chat/chat-messages/CitationBadge";
import { SourcesFooter } from "@/components/chat/chat-messages/SourcesFooter";
import { processCitations, normalizeAnnotations, isCitationLink } from "@/lib/chat/citations";
import type { CitationSource } from "@/lib/chat/citations";
import type { ContentItem } from "@/types/content";
import { useAuth } from "@/hooks/useAuth";
import type { Plan } from "@/lib/ai/models";
import type { RoomChatMessage } from "@/lib/chat/roomChatMessages";

const BASE_SYSTEM_PROMPT = `You are Shattara AI, a friendly, focused study tutor for high school and university students in Saudi Arabia and the MENA region.

Core behavior:
- Explain concepts clearly, step-by-step, using simple language first, then add deeper detail if needed.
- Encourage active learning: ask short follow-up questions, propose practice questions, and suggest ways to check understanding.
- Adapt depth and tone to the student's level based on how they ask the question.
- When a question is ambiguous, briefly ask a clarifying question instead of guessing.
- When the student is stuck, break the problem into small steps and guide them through.
- Prioritize correctness over creativity. If you are unsure, say so and suggest how the student can verify.

Content handling:
- Use the provided content as your PRIMARY source when answering questions.
- Reference specific parts of the content when relevant.
- If the student asks about something not covered in the content, say so clearly.
- Quote or paraphrase from the content to support your explanations.
- If there is a conflict between your general knowledge and the provided material, explain both and say which is which.
- For math or science derivations, always show key steps, not just the final answer.
- When the user mentions a specific content item by name (with @), focus your answer on that particular content.

Safety and integrity:
- Do not write full graded assignments or essays for the student. Instead, help them outline, brainstorm, and improve their own work.
- Do not invent citations, references, or fake sources.
- Avoid harmful, hateful, or adult content and redirect to safe, educational topics.

Formatting:
- Use short paragraphs and bullets when it helps readability.
- Use inline LaTeX-style math (like x^2 + y^2) only when it's helpful, not everywhere.
- For definitions or procedures, you can use numbered steps (1., 2., 3., …).

Always keep your goal in mind:
Help the student understand, not just memorize; make them feel more confident about studying.

Study tools:
When the user mentions @Mind Map, respond with a mindmap code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the mind map.
IMPORTANT constraints:
- Maximum 4-5 top-level nodes (branches from root). Never exceed 5.
- Maximum 2-3 children per node. Prioritize the most important points.
- Keep labels short (1-3 words).

\`\`\`mindmap
{
  "title": "Topic Title",
  "root": "Central Concept",
  "nodes": [
    { "id": "1", "label": "Branch 1", "children": [
      { "id": "1a", "label": "Sub-point A" },
      { "id": "1b", "label": "Sub-point B" }
    ]},
    { "id": "2", "label": "Branch 2" }
  ],
  "description": "Brief summary of the mind map."
}
\`\`\`

When the user mentions @Timeline, respond with a timeline code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the timeline.
IMPORTANT constraints:
- Maximum 8-10 items. Prioritize the most significant events.
- Keep titles short (2-5 words).
- Dates should be concise (e.g. "1889", "1914-1918", "June 1944").

\`\`\`timeline
{
  "title": "Timeline Title",
  "items": [
    { "id": "1", "date": "1900", "title": "Event Title", "description": "Brief description" },
    { "id": "2", "date": "1950", "title": "Another Event", "description": "What happened" }
  ]
}
\`\`\`

When the user mentions @Flowchart, respond with a flowchart code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the flowchart.
IMPORTANT constraints:
- Maximum 8-12 nodes. Prioritize key steps and decision points.
- Use "rect" shape for events/actions, "diamond" for yes/no decisions.
- Keep labels short (1-3 words per line, max 3 lines).
- Use colors meaningfully: blue for start, green for positive, purple for actions, orange for transitions, yellow for outcomes.
- Edges connect nodes by id. Add "label" only for decision branches (e.g. "Yes", "No").

\`\`\`flowchart
{
  "title": "Process Title",
  "nodes": [
    { "id": "A", "label": ["Step One", "Detail"], "shape": "rect", "color": "blue" },
    { "id": "B", "label": ["Decision?"], "shape": "diamond", "color": "green" },
    { "id": "C", "label": ["Result"], "shape": "rect", "color": "purple" }
  ],
  "edges": [
    { "from": "A", "to": "B" },
    { "from": "B", "to": "C", "label": "Yes" }
  ]
}
\`\`\`

When the user mentions @Bar Chart, respond with a barchart code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the chart.
IMPORTANT constraints:
- Maximum 10 data points (labels). Prioritize the most significant values.
- Keep labels short (1-4 words).
- Keep axis labels concise (1-3 words).
- Use descriptive dataset labels.
- Default color is "#f59e0b" (amber). Use different colors for multiple datasets.

\`\`\`barchart
{
  "title": "Chart Title",
  "xLabel": "Category",
  "yLabel": "Value",
  "labels": ["Item A", "Item B", "Item C"],
  "datasets": [
    { "label": "Count", "data": [10, 25, 15], "color": "#f59e0b" }
  ]
}
\`\`\`

When the user mentions @Line Chart, respond with a linechart code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the chart.
IMPORTANT constraints:
- Maximum 10 data points (labels). Prioritize the most significant values.
- Keep labels short (1-4 words).
- Keep axis labels concise (1-3 words).
- Use descriptive dataset labels.
- Default color is "#f59e0b" (amber). Use different colors for multiple datasets.
- Supports multiple lines (datasets) for comparison.

\`\`\`linechart
{
  "title": "Chart Title",
  "xLabel": "Time",
  "yLabel": "Value",
  "labels": ["Jan", "Feb", "Mar"],
  "datasets": [
    { "label": "Series A", "data": [10, 25, 15], "color": "#f59e0b" }
  ]
}
\`\`\`

When the user mentions @Pie Chart, respond with a piechart code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the chart.
IMPORTANT constraints:
- Maximum 8 slices. Combine smaller items into "Other" if needed.
- Keep labels short (1-4 words).
- Data values can be percentages or absolute numbers.
- Default colors: "#f59e0b", "#ef4444", "#3b82f6", "#10b981", "#f97316".

\`\`\`piechart
{
  "title": "Chart Title",
  "labels": ["Category A", "Category B", "Category C"],
  "data": [50, 30, 20]
}
\`\`\``;

interface RoomAITutorPanelProps {
  isOpen: boolean;
  onClose: () => void;
  roomId?: string;
  teamId?: string | null;
  contents?: ContentItem[];
}

interface Message {
  id?: string;
  role: "user" | "assistant";
  content: string;
  metadata?: any;
  userId?: string;
  senderName?: string | null;
  senderEmail?: string | null;
}

// Consistent avatar color based on userId
const AVATAR_COLORS = [
  "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500",
  "bg-violet-500", "bg-cyan-500", "bg-orange-500", "bg-teal-500",
];

function getAvatarColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitial(name?: string | null, email?: string | null): string {
  if (name) return name.charAt(0).toUpperCase();
  if (email) return email.charAt(0).toUpperCase();
  return "?";
}

const ROLE_BADGE_STYLES: Record<string, string> = {
  owner: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  admin: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  member: "bg-muted text-muted-foreground",
};

const ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
};

export const RoomAITutorPanel: React.FC<RoomAITutorPanelProps> = ({
  isOpen,
  onClose,
  roomId,
  teamId,
  contents = [],
}) => {
  const { t, isRtl } = useTranslation();
  const { user } = useAuth();
  const [selectedModel, setSelectedModel] = useState("auto");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [mentionPickerOpen, setMentionPickerOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [likedMessages, setLikedMessages] = useState<Set<number>>(new Set());
  const [userPlan, setUserPlan] = useState<Plan>("free");
  const [teamRoles, setTeamRoles] = useState<Map<string, string>>(new Map());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasLoadedRef = useRef<string | null>(null);

  // Fetch subscription status
  useEffect(() => {
    if (!user) return;
    const fetchPlan = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("subscription_status")
        .eq("id", user.id)
        .single();
      if (data?.subscription_status === "active") {
        setUserPlan("pro");
      }
    };
    fetchPlan();
  }, [user]);

  // Aggregate content text for grounding
  const { contentText, isLoading: isLoadingContent, groundedCount } = useRoomContentText(contents);

  // Load persisted messages when panel opens or roomId changes
  useEffect(() => {
    if (!isOpen || !roomId || hasLoadedRef.current === roomId) return;

    const loadMessages = async () => {
      setIsLoadingHistory(true);
      try {
        const saved = await loadRoomChatMessages(roomId);
        const mapped: Message[] = saved.map((m) => ({
          id: m.id,
          role: m.role as "user" | "assistant",
          content: m.message,
          userId: m.user_id,
          senderName: m.sender_name,
          senderEmail: m.sender_email,
        }));
        setMessages(mapped);
        hasLoadedRef.current = roomId;
      } catch (error) {
        console.error("Error loading chat history:", error);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadMessages();
  }, [isOpen, roomId]);

  // Realtime subscription for team room chat messages
  useEffect(() => {
    if (!isOpen || !roomId || !teamId) return;

    const channel = supabase
      .channel(`room-chat-${roomId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'room_chat_messages',
          filter: `room_id=eq.${roomId}`,
        },
        async (payload) => {
          const newRow = payload.new as any;
          // Skip messages from the current user (already added optimistically)
          if (newRow.user_id === user?.id) return;

          // Fetch sender profile
          const { data: profile } = await supabase
            .from("profiles")
            .select("name, email")
            .eq("id", newRow.user_id)
            .single();

          const newMsg: Message = {
            id: newRow.id,
            role: newRow.role as "user" | "assistant",
            content: newRow.message,
            userId: newRow.user_id,
            senderName: profile?.name ?? null,
            senderEmail: profile?.email ?? null,
          };
          setMessages(prev => [...prev, newMsg]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isOpen, roomId, teamId, user?.id]);

  // Fetch team roles when teamId is present
  useEffect(() => {
    if (!teamId) {
      setTeamRoles(new Map());
      return;
    }
    const fetchRoles = async () => {
      const { data } = await (supabase as any)
        .from("team_members")
        .select("user_id, role")
        .eq("team_id", teamId);
      if (data) {
        const map = new Map<string, string>();
        for (const row of data) {
          map.set(row.user_id, row.role);
        }
        setTeamRoles(map);
      }
    };
    fetchRoles();
  }, [teamId]);

  // Build system prompt with or without content grounding
  const getSystemPrompt = useCallback((): string => {
    if (!contentText) return BASE_SYSTEM_PROMPT;

    return `${BASE_SYSTEM_PROMPT}

## Room Study Materials
You have access to the following content from this study room.
Use these materials as your PRIMARY source when answering questions.

${contentText}`;
  }, [contentText]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleNewConversation = async () => {
    if (!roomId) return;
    
    // Clear local state immediately
    setMessages([]);
    
    // Clear from DB
    const success = await clearRoomChatMessages(roomId);
    if (!success) {
      toast.error("Failed to clear conversation history");
    }
  };

  // Handle @mention content selection
  const handleContentMention = (item: ContentItem) => {
    setMentionPickerOpen(false);
    setMentionFilter("");
    // The content title will be injected into the input as @ContentTitle
    // We return the text to prepend
    return `@${item.title} `;
  };

  const handleSendMessage = async (text: string, attachments: File[]) => {
    const trimmedInput = text.trim();
    if (!trimmedInput || isStreaming) return;

    const userMessage: Message = { role: "user", content: trimmedInput, userId: user?.id, senderName: null, senderEmail: user?.email };
    setMessages(prev => [...prev, userMessage]);
    setIsStreaming(true);

    // Persist user message
    if (roomId) {
      saveRoomChatMessage({
        roomId,
        role: "user",
        message: trimmedInput,
      });
    }

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        toast.error("Please sign in to use the AI tutor.");
        setIsStreaming(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            messages: [
              { role: "system", content: getSystemPrompt() },
              ...messages.map(m => ({ role: m.role, content: m.content })),
              { role: "user", content: trimmedInput },
            ],
            searchEnabled: isSearchEnabled,
          }),
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get response: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let assistantContent = "";
      const collectedAnnotations: any[] = [];

      setMessages(prev => [...prev, { role: "assistant", content: "" }]);

      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();
            if (data === "[DONE]") continue;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices?.[0]?.delta?.content;
              if (content) {
                assistantContent += content;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = {
                    role: "assistant",
                    content: assistantContent,
                  };
                  return updated;
                });
              }
              const annotations = parsed.choices?.[0]?.delta?.annotations;
              if (annotations && Array.isArray(annotations)) {
                collectedAnnotations.push(...annotations);
              }
            } catch {
              // Ignore parse errors
            }
          }
        }
      }

      // Persist assistant message after streaming completes
      if (roomId && assistantContent) {
        // Update last message with annotations metadata
        if (collectedAnnotations.length > 0) {
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              metadata: { annotations: collectedAnnotations },
            };
            return updated;
          });
        }
        saveRoomChatMessage({
          roomId,
          role: "assistant",
          message: assistantContent,
          metadata: collectedAnnotations.length > 0 ? { annotations: collectedAnnotations } : undefined,
        });
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      console.error("Chat error:", error);
      toast.error("Failed to get AI response. Please try again.");
      setMessages(prev => {
        if (prev.length > 0 && prev[prev.length - 1].role === "assistant" && !prev[prev.length - 1].content) {
          return prev.slice(0, -1);
        }
        return prev;
      });
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const handleCopyMessage = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Failed to copy");
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile/Tablet backdrop */}
      <div 
        className="fixed inset-0 z-40 bg-background/80 xl:hidden"
        onClick={onClose}
        aria-label="Close tutor backdrop"
      />

      {/* Panel */}
      <aside 
        className={`fixed right-0 top-0 z-50 flex h-screen flex-col overflow-y-auto scrollbar-hide border-l border-primary/5 bg-background shadow-2xl transition-all duration-300 ease-in-out ${
          isFullscreen ? 'w-full shadow-none' : 'w-full xl:w-[576px]'
        }`}
        style={{
          boxShadow: isFullscreen 
            ? 'none' 
            : '-10px 0 40px -10px rgba(0, 0, 0, 0.1), -5px 0 20px -5px rgba(0, 0, 0, 0.05)'
        }}
      >
        <div className="relative flex h-full flex-col overflow-y-auto scrollbar-hide px-1 pb-1 pt-4">
          <div className="mb-7 ml-4 mr-2 flex items-center justify-between">
            <div className="text-muted-foreground transition-opacity duration-300 ease-in-out hover:text-foreground">
              <X
                onClick={onClose}
                className="h-5 w-5 cursor-pointer"
                aria-label="Close"
              />
            </div>

            <div className="flex items-center gap-2">
              {contents.length > 0 && !isLoadingContent && (
                <div className="rounded-full bg-primary/5 px-2.5 py-1 text-xs text-muted-foreground">
                  {contents.length} {contents.length === 1 ? 'source' : 'sources'}
                </div>
              )}
              {isLoadingContent && contents.length > 0 && (
                <div className="flex items-center gap-1.5 rounded-full bg-primary/5 px-2.5 py-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Loading sources…</span>
                </div>
              )}

              <div className="text-muted-foreground transition-opacity duration-300 ease-in-out hover:text-foreground">
                <button 
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-transparent px-3 text-sm font-medium text-primary ring-offset-background transition-colors hover:bg-transparent focus-visible:outline-none"
                  aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                >
                  {isFullscreen ? (
                    <Minimize className="h-4 w-4" />
                  ) : (
                    <Maximize className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="flex-grow overflow-y-auto scrollbar-hide">
            <div className="relative flex h-full w-full flex-col px-0 md:h-[calc(100vh-84px)]">
              <div className="absolute right-0 top-0 z-40 mt-1 xl:-mt-2">
                <TooltipProvider>
                  <div className="flex items-center">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={handleNewConversation}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-primary/60 ring-offset-background transition-colors hover:bg-primary/5 hover:text-accent-foreground focus-visible:outline-none"
                          aria-label="New conversation"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent><p>New Chat</p></TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl px-4 py-2 text-primary/60 ring-offset-background transition-colors hover:bg-primary/5 hover:text-accent-foreground focus-visible:outline-none"
                          aria-label="History"
                        >
                          <History className="h-3.5 w-3.5 shrink-0" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent><p>Chat History</p></TooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>
              </div>

              <div data-chat-container="true" className="flex flex-1 flex-col overflow-y-auto scrollbar-hide overscroll-y-none" style={{ overflowAnchor: 'none' }}>
                <div className="mx-auto w-full max-w-3xl"></div>
                
                {isLoadingHistory ? (
                  <div className="flex flex-1 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="mx-auto mt-10 flex w-full max-w-3xl flex-grow flex-col justify-center px-4 sm:px-4">
                    <div className="flex flex-col items-center justify-center p-4 text-center text-primary/40 dark:text-primary/60">
                      <MessageCircle className="mb-4 h-12 w-12 opacity-40" />
                      <p className="mb-1 text-base font-medium text-primary/40 dark:text-primary/60">
                        {t("tutor.learnWithTutor")}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mx-auto flex w-full max-w-3xl flex-grow flex-col px-4 sm:px-4 mt-0 justify-start">
                    <div className="w-full">
                      <div className="space-y-8">
                        {messages.map((message, index) => (
                          <div key={message.id || index} className="flex flex-col gap-2">
                            {message.role === "user" ? (
                              <div className="flex w-full justify-end">
                                <div className="group/message flex w-full flex-col items-end gap-1">
                                  {/* Sender identity */}
                                  {(() => {
                                    const isOwnMessage = message.userId === user?.id;
                                    const displayName = isOwnMessage ? "You" : (message.senderName || message.senderEmail || "User");
                                    const initial = getInitial(message.senderName, message.senderEmail);
                                    const avatarColor = message.userId ? getAvatarColor(message.userId) : "bg-muted";
                                    const role = message.userId ? teamRoles.get(message.userId) : undefined;
                                    return (
                                      <div className="flex items-center gap-1.5 mb-1">
                                        {teamId && role && (
                                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${ROLE_BADGE_STYLES[role] || ROLE_BADGE_STYLES.member}`}>
                                            {ROLE_LABELS[role] || role}
                                          </span>
                                        )}
                                        <span className="text-xs font-medium text-muted-foreground">{displayName}</span>
                                        <div className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold text-white ${avatarColor}`}>
                                          {initial}
                                        </div>
                                      </div>
                                    );
                                  })()}
                                  <div className="flex w-full justify-end">
                                    <div className="relative w-fit rounded-3xl p-3 text-left leading-relaxed text-primary/95 border border-primary/5 bg-primary/5 dark:bg-neutral-800">
                                      <div className="markdown-body prose prose-neutral dark:prose-invert max-w-none">
                                        <div className="space-y-4">
                                          <p className="text-base leading-7 last:mb-0 whitespace-pre-wrap">{message.content}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 opacity-100 transition-opacity duration-200 lg:opacity-0 lg:group-hover/message:opacity-100">
                                    <button 
                                      className="rounded-full p-1 transition-colors hover:bg-primary/10" 
                                      aria-label="Edit message"
                                    >
                                      <Pen className="h-3 w-3 text-primary/60" aria-hidden="true" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="overflow-x-auto">
                                <div className="flex w-full flex-col">
                                  <div className="w-full">
                                    <div className="flex w-full justify-start">
                                      <div className="w-full" style={{ position: "relative" }}>
                                        <div className="relative rounded-3xl text-left leading-relaxed text-primary/95 w-full group bg-transparent p-0 pt-1">
                                          <div className="markdown-body prose prose-neutral dark:prose-invert max-w-none">
                                            <div className="space-y-4 inline">
                                              <ReactMarkdown
                                                components={{
                                                  p: ({ children }) => (
                                                    <p className="text-base leading-7 last:mb-0">{children}</p>
                                                  ),
                                                  h2: ({ children }) => (
                                                    <h2 className="mb-3 mt-6 text-xl font-semibold first:mt-0">{children}</h2>
                                                  ),
                                                  h3: ({ children }) => (
                                                    <h3 className="mb-2 mt-5 text-lg font-semibold first:mt-0">{children}</h3>
                                                  ),
                                                  ul: ({ children }) => (
                                                    <ul className="list-outside list-disc pl-5">{children}</ul>
                                                  ),
                                                  ol: ({ children }) => (
                                                    <ol className="list-outside list-decimal pl-5">{children}</ol>
                                                  ),
                                                  li: ({ children }) => (
                                                    <li className="leading-7">{children}</li>
                                                  ),
                                                  blockquote: ({ children }) => (
                                                    <blockquote className="my-6 rounded-r-md border-l-4 border-border/50 bg-muted/30 px-6 py-3 dark:border-primary/10">{children}</blockquote>
                                                  ),
                                                  hr: () => (
                                                    <hr className="my-8 border-t border-border/50 dark:border-primary/10" />
                                                  ),
                                                  strong: ({ children }) => (
                                                    <strong className="font-semibold">{children}</strong>
                                                  ),
                                                  code: ({ children }) => (
                                                    <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono">{children}</code>
                                                  ),
                                                }}
                                              >
                                                {message.content || "..."}
                                              </ReactMarkdown>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  {message.content && (
                                    <div className="py-3">
                                      <div className="flex flex-row items-center justify-between space-x-1">
                                        <div className="flex flex-row items-center space-x-1">
                                          <button 
                                            onClick={() => handleCopyMessage(message.content)}
                                            className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none hover:bg-accent hover:text-accent-foreground h-7 w-7 p-1.5 text-muted-foreground"
                                            aria-label="Copy message"
                                          >
                                            <Copy className="h-4 w-4" aria-hidden="true" />
                                          </button>
                                          <button 
                                            onClick={() => {
                                              setLikedMessages(prev => new Set(prev).add(index));
                                              toast.success("Thanks for your feedback");
                                            }}
                                            className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none hover:bg-accent hover:text-accent-foreground h-7 w-7 p-1.5 text-muted-foreground"
                                            aria-label="Like response"
                                          >
                                            <ThumbsUp className="h-4 w-4" fill={likedMessages.has(index) ? "currentColor" : "none"} aria-hidden="true" />
                                          </button>
                                          <FeedbackPopover>
                                            <button 
                                              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none hover:bg-accent hover:text-accent-foreground h-7 w-7 p-1.5 text-muted-foreground"
                                              aria-label="Dislike response"
                                            >
                                              <ThumbsDown className="h-4 w-4" aria-hidden="true" />
                                            </button>
                                          </FeedbackPopover>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        {isStreaming && messages[messages.length - 1]?.role === "assistant" && !messages[messages.length - 1]?.content && (
                          <div className="flex justify-start">
                            <div className="bg-transparent p-0 pt-1">
                              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            </div>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative mx-auto mt-0 w-full max-w-3xl px-2 sm:px-0">
                {/* Content mention picker */}
                <RoomContentMentionPicker
                  contents={contents}
                  isOpen={mentionPickerOpen}
                  onSelect={(item) => {
                    // We close the picker; the text area should already have @ typed
                    setMentionPickerOpen(false);
                    setMentionFilter("");
                  }}
                  onClose={() => {
                    setMentionPickerOpen(false);
                    setMentionFilter("");
                  }}
                  filter={mentionFilter}
                />
                <ChatInputBar
                  onSendMessage={handleSendMessage}
                  disabled={isStreaming}
                  placeholder="Ask about your content"
                  selectedModel={selectedModel}
                  onModelChange={setSelectedModel}
                  showModelSelector={true}
                  showContextButton={true}
                  showAttachments={false}
                  showMicButton={false}
                  onSearchStateChange={setIsSearchEnabled}
                  userPlan={userPlan}
                />
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};
